import React, { useState } from 'react';
import { Box, Icon } from '@gluestack-ui/themed';
import { Pencil } from 'lucide-react-native';
import ContentGroup from '@/common/components/ContentGroup';
import FormField from './FormField';

/**
 * SimpleRTE - A minimal rich text editor with ContentGroup styling.
 *
 * Sizing modes:
 * - Fixed: pass `height` (legacy). Editor is exactly that tall and scrolls
 *   internally if content is longer.
 * - Grow: pass `minHeight` AND `maxHeight`. Editor sizes to its content
 *   between the bounds; native scrollbar chrome is hidden once content
 *   exceeds maxHeight. Preferred for read-mostly feedback surfaces.
 */
interface SimpleRTEProps {
  name: string;
  placeholder?: string;
  readonly?: boolean;
  height?: number;
  minHeight?: number;
  maxHeight?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rules?: any;
  bg?: string;
  showAccentBar?: boolean;
  hideToolbar?: boolean;
  flex?: number;
  /** Resting chrome around the editor so it reads as a form, not a document. */
  restingBorderColor?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

const SimpleRTE: React.FC<SimpleRTEProps> = ({
  name,
  placeholder = 'Enter text...',
  readonly = false,
  height,
  minHeight,
  maxHeight,
  rules,
  bg = '$backgroundLight100',
  showAccentBar = false,
  hideToolbar = true,
  restingBorderColor = 'transparent',
  ...props
}) => {
  const useGrow = minHeight != null && maxHeight != null;
  // In grow mode, fieldHeight is unused; in fixed mode keep the legacy default.
  const resolvedHeight = useGrow ? undefined : (height ?? 80);
  const [isFocused, setIsFocused] = useState(false);

  // Cast components to any to bypass strict prop checking for JSX components in TSX
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const FormFieldAny = FormField as any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const BoxAny = Box as any;

  return (
    <ContentGroup
      variant={showAccentBar ? 'bordered' : 'flush'}
      bg={bg}
      accentWidth={4}
      p="$0"
      borderWidth={1}
      borderColor={isFocused ? '$borderBrand' : restingBorderColor}
      flex={props.flex}
    >
      <Box p="$2" position="relative">
        {/* Reserve right padding so the absolute pencil indicator never
            overlaps the wrapped editor content. */}
        <BoxAny
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          pr={!readonly ? '$5' : '$0'}
        >
          <FormFieldAny
            name={name}
            type="rte"
            labelSize="xs"
            fieldSize="xs"
            readonly={readonly}
            fieldHeight={resolvedHeight}
            fieldMinHeight={useGrow ? minHeight : undefined}
            fieldMaxHeight={useGrow ? maxHeight : undefined}
            borderless
            hideToolbar={hideToolbar}
            placeholder={placeholder}
            errorTextSize="2xs"
            rules={rules}
            {...props}
          />
        </BoxAny>
        {!readonly && (
          <Box
            position="absolute"
            bottom="$2"
            right="$2"
            sx={{ pointerEvents: 'none' }}
          >
            <Icon as={Pencil} size="xs" color="$textSubtle" />
          </Box>
        )}
      </Box>
    </ContentGroup>
  );
};

export default SimpleRTE;
